<template>
  <div
    v-if="visible"
    class="smart-dimension-popover"
    :style="{ left: `${x}px`, top: `${y}px` }"
    @pointerdown.stop
    @click.stop
  >
    <template v-if="mode === 'input'">
      <div class="popover-title">修改</div>
      <div class="popover-row">
        <n-input
          ref="inputRef"
          v-model:value="inputValue"
          class="popover-input"
          size="small"
          :placeholder="isAngle ? '如 45 或 90/2' : '如 60 或 30mm+2in'"
          @keydown.enter.prevent="handleConfirm"
          @keydown.esc.stop.prevent="emit('cancel')"
        >
          <template #suffix>{{ isAngle ? '°' : 'mm' }}</template>
        </n-input>
      </div>
      <div
        v-for="group in arcConditionGroups ?? []"
        :key="group.selectionOrder"
        class="popover-arc-conditions"
      >
        <span class="popover-arc-label">附着{{ group.selectionOrder + 1 }}</span>
        <n-button
          v-for="option in ARC_CONDITION_OPTIONS"
          :key="option.value"
          size="tiny"
          :type="group.current === option.value ? 'primary' : 'default'"
          :quaternary="group.current !== option.value"
          @click="emit('change-arc-condition', group.selectionOrder, option.value)"
        >{{ option.label }}</n-button>
      </div>
      <div class="popover-actions">
        <n-button
          v-if="roleToggleLabel"
          size="tiny"
          tertiary
          @click="emit('toggle-role')"
        >{{ roleToggleLabel }}</n-button>
        <n-button size="tiny" type="primary" @click="handleConfirm">确定</n-button>
        <n-button size="tiny" quaternary @click="emit('cancel')">取消</n-button>
      </div>
    </template>
    <template v-else>
      <div class="popover-title">是否设为从动尺寸？</div>
      <div class="popover-message">{{ decisionMessage }}</div>
      <div class="popover-actions">
        <n-button size="tiny" type="primary" @click="emit('convert-driven')">设为从动</n-button>
        <n-button size="tiny" quaternary @click="emit('cancel')">取消</n-button>
      </div>
    </template>
  </div>
</template>

<script setup lang="ts">
/**
 * 智能尺寸 Modify 浮层。
 *
 * 设计说明：
 * 1. 对齐 SOLIDWORKS：新建尺寸在放置位置附近弹出数值输入，而不是塞进左侧
 *    PropertyManager（02 计划 §11.3）；
 * 2. 输入是自由文本（支持算术表达式与单位后缀，VAL-001/005），组件只负责
 *    收集原文并上抛，解析/单位边界在宿主（`parseSketchDimensionValueInput`）；
 * 3. `decision` 模式承载过定义/无解决策（转从动 / 取消）。
 */
import { nextTick, ref, watch } from 'vue';
import { NButton, NInput } from 'naive-ui';

const props = defineProps<{
  /** 是否可见。 */
  visible: boolean;
  /** 浮层屏幕 X（px）。 */
  x: number;
  /** 浮层屏幕 Y（px）。 */
  y: number;
  /** 浮层模式：数值输入 / 过定义决策。 */
  mode: 'input' | 'decision';
  /** 初始显示值（长度 mm / 角度 °）。 */
  initialValue: number;
  /** 是否角度尺寸。 */
  isAngle: boolean;
  /** 决策提示消息。 */
  decisionMessage?: string;
  /** 驱动角色切换按钮文案（编辑已有尺寸时显示，如"设为从动"）；省略时不显示。 */
  roleToggleLabel?: string;
  /** 圆系引用附着条件组（Center/Min/Max 切换，ARC-002）；省略时不显示。 */
  arcConditionGroups?: {selectionOrder: number; current: 'center' | 'min' | 'max'}[];
}>();

const emit = defineEmits<{
  (event: 'confirm', rawText: string): void;
  (event: 'cancel'): void;
  (event: 'convert-driven'): void;
  (event: 'toggle-role'): void;
  (event: 'change-arc-condition', selectionOrder: number, condition: 'center' | 'min' | 'max'): void;
}>();

/** 附着条件选项（对照 SOLIDWORKS Center/Min/Max）。 */
const ARC_CONDITION_OPTIONS = [
  {value: 'center', label: '中心'},
  {value: 'min', label: '最近'},
  {value: 'max', label: '最远'},
] as const;

/** 输入文本（显示单位默认 mm/°，可含表达式与单位后缀）。 */
const inputValue = ref<string>(String(props.initialValue));
/** 输入框引用（弹出时自动聚焦全选）。 */
const inputRef = ref<InstanceType<typeof NInput> | null>(null);

watch(
  () => [props.visible, props.initialValue] as const,
  async ([visible, initialValue]) => {
    if (!visible) {
      return;
    }
    inputValue.value = String(initialValue);
    await nextTick();
    inputRef.value?.select?.();
    inputRef.value?.focus?.();
  },
  { immediate: true },
);

/** 确认输入：原文上抛，合法性由宿主解析器判定并提示。 */
function handleConfirm(): void {
  if (inputValue.value.trim().length === 0) {
    return;
  }
  emit('confirm', inputValue.value);
}
</script>

<style scoped>
.smart-dimension-popover {
  position: fixed;
  z-index: 60;
  min-width: 168px;
  padding: 8px 10px;
  border-radius: 6px;
  background: var(--pc-panel-bg, #2b2b2f);
  border: 1px solid rgba(255, 255, 255, 0.12);
  box-shadow: 0 6px 18px rgba(0, 0, 0, 0.35);
}

.popover-title {
  font-size: 12px;
  color: #cfd3dc;
  margin-bottom: 6px;
}

.popover-message {
  font-size: 12px;
  color: #e8b339;
  max-width: 220px;
  margin-bottom: 6px;
  word-break: break-all;
}

.popover-row {
  margin-bottom: 8px;
}

.popover-input {
  width: 100%;
}

.popover-actions {
  display: flex;
  gap: 8px;
  justify-content: flex-end;
}

.popover-arc-conditions {
  display: flex;
  align-items: center;
  gap: 4px;
  margin-bottom: 6px;
}

.popover-arc-label {
  font-size: 12px;
  color: #9a9ea8;
  margin-right: 2px;
}
</style>
